import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Challenge, ExerciseLog, Participant } from '@/types';
import { apiClient } from '@/lib/api';
import { useUserStore } from '@/stores/userStore';
import { useToast } from '@/components/ui/Toast';

export const useChallenges = () => {
  const { user, isLoading: userLoading } = useUserStore();

  const result = useQuery({
    queryKey: ['challenges', user?.id],
    queryFn: async () => {
      if (!user) {
        return [];
      }
      return await apiClient.getUserChallenges(user.id);
    },
    enabled: !!user && !userLoading,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: 2
  });

  // Return loading if user is still loading OR query is loading
  return {
    ...result,
    isLoading: userLoading || result.isLoading,
  };
};

export const useChallenge = (challengeId: string) => {
  return useQuery({
    queryKey: ['challenge', challengeId],
    queryFn: () => apiClient.getChallenge(challengeId),
    enabled: !!challengeId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useCreateChallenge = () => {
  const queryClient = useQueryClient();
  const { user } = useUserStore();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (challengeData: Omit<Challenge, 'id' | 'createdAt' | 'updatedAt'>) => {
      return apiClient.createChallenge(challengeData);
    },
    onSuccess: newChallenge => {
      // Update challenges cache
      queryClient.setQueryData(['challenges', user?.id], (old: Challenge[] = []) => {
        return [...old, newChallenge];
      });

      // Add to individual challenge cache
      queryClient.setQueryData(['challenge', newChallenge.id], newChallenge);

      // Invalidate participants cache to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['participants', newChallenge.id] });

      addToast({
        type: 'success',
        message: 'Challenge created successfully!',
      });
    },
    onError: error => {
      addToast({
        type: 'error',
        message: 'Failed to create challenge. Please try again.',
      });
    },
  });
};

export const useJoinChallenge = () => {
  const queryClient = useQueryClient();
  const { user } = useUserStore();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({ challengeId, userId }: { challengeId: string; userId: string }) => {
      return apiClient.joinChallenge(challengeId, userId);
    },
    onSuccess: updatedChallenge => {
      // Update challenges cache
      queryClient.setQueryData(['challenges', user?.id], (old: Challenge[] = []) => {
        const existingIndex = old.findIndex(c => c.id === updatedChallenge.id);
        if (existingIndex >= 0) {
          const newChallenges = [...old];
          newChallenges[existingIndex] = updatedChallenge;
          return newChallenges;
        }
        return [...old, updatedChallenge];
      });

      // Update individual challenge cache
      queryClient.setQueryData(['challenge', updatedChallenge.id], updatedChallenge);

      // Invalidate participants cache to get fresh data
      queryClient.invalidateQueries({ queryKey: ['participants', updatedChallenge.id] });

      addToast({
        type: 'success',
        message: 'Successfully joined challenge!',
      });
    },
    onError: error => {
      addToast({
        type: 'error',
        message: 'Failed to join challenge. Please try again.',
      });
    },
  });
};

export const useExerciseLogs = (challengeId: string) => {
  return useQuery({
    queryKey: ['exerciseLogs', challengeId],
    queryFn: () => apiClient.getExerciseLogsForChallenge(challengeId),
    enabled: !!challengeId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const useLogExercise = (challengeId: string, challenge?: Challenge) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { user } = useUserStore();

  return useMutation({
    mutationFn: (exerciseData: Omit<ExerciseLog, 'id'>) => {
      return apiClient.logExercise(exerciseData, challenge);
    },
    onMutate: async (exerciseData) => {
      // Cancel any outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['exerciseLogs', challengeId] });
      await queryClient.cancelQueries({ queryKey: ['participants', challengeId] });

      // Snapshot previous values for rollback
      const previousLogs = queryClient.getQueryData<ExerciseLog[]>(['exerciseLogs', challengeId]);
      const previousParticipants = queryClient.getQueryData<Participant[]>(['participants', challengeId]);

      // Optimistically update exercise logs for immediate feedback
      const optimisticLog: ExerciseLog = {
        ...exerciseData,
        id: `temp-${Date.now()}`,
      };
      
      queryClient.setQueryData<ExerciseLog[]>(['exerciseLogs', challengeId], old => {
        return [...(old || []), optimisticLog];
      });

      // Optimistically update participant data for immediate UI feedback
      if (exerciseData.userId) {
        queryClient.setQueryData<Participant[]>(['participants', challengeId], old => {
          if (!old) return old;
          
          return old.map(participant => {
            if (participant.userId === exerciseData.userId) {
              // Update the participant's total amount and daily log
              const today = new Date().toISOString().split('T')[0];
              const currentDailyAmount = participant.dailyLog[today] || 0;
              const newDailyAmount = currentDailyAmount + exerciseData.amount;
              
              return {
                ...participant,
                totalAmount: participant.totalAmount + exerciseData.amount,
                dailyLog: {
                  ...participant.dailyLog,
                  [today]: newDailyAmount
                },
                sets: {
                  ...participant.sets,
                  [`${today}_sets`]: [
                    ...(participant.sets[`${today}_sets`] || []),
                    exerciseData.amount
                  ]
                }
              };
            }
            return participant;
          });
        });
      }

      return { previousLogs, previousParticipants };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousLogs) {
        queryClient.setQueryData(['exerciseLogs', challengeId], context.previousLogs);
      }
      if (context?.previousParticipants) {
        queryClient.setQueryData(['participants', challengeId], context.previousParticipants);
      }
      
      addToast({
        type: 'error',
        message: 'Failed to log exercise. Please try again.',
      });
    },
    onSuccess: async (newLog) => {
      // Update the optimistic log with real data
      queryClient.setQueryData<ExerciseLog[]>(['exerciseLogs', challengeId], old => {
        if (!old) return [newLog];
        // Replace temp log with real log
        return old.map(log => log.id.startsWith('temp-') ? newLog : log);
      });

      // Invalidate to get fresh server data (including other users' updates)
      // The optimistic update stays until this resolves
      queryClient.invalidateQueries({ queryKey: ['participants', challengeId] });
      
      // Also invalidate batch participants query used by homepage
      queryClient.invalidateQueries({ queryKey: ['batchParticipants'] });
      
      // Also invalidate user's challenges list so homepage reflects updated stats
      if (user) {
        queryClient.invalidateQueries({ queryKey: ['challenges', user.id] });
      }
    },
  });
};

// Hook to get participants with usernames from the server
export const useParticipants = (challengeId: string) => {
  return useQuery({
    queryKey: ['participants', challengeId],
    queryFn: () => apiClient.getChallengeParticipants(challengeId),
    enabled: !!challengeId,
    staleTime: 10 * 1000, // 10 seconds - matches polling interval
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchInterval: false, // Polling handled by usePolling hook
    refetchOnWindowFocus: true, // Refresh when user returns to tab
    refetchOnMount: true, // Get fresh data on mount
    refetchOnReconnect: true, // Refresh when reconnected
    retry: 2, // Retry failed requests
  });
};
